// src/pages/LoginPage.js

import React, { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthRedirect } from "../hooks/useAuthRedirect";
import { AuthContext } from "../context/AuthContext";
import GoogleSignInButton from "../components/GoogleSignInButton";
import logoImg from "../assets/images/logowhite.png";
import { CheckCircle2, Sparkles } from "lucide-react";
import Config from "../Config.json";
import ICON from "../assets/icons/favicon.ico";

const TITLE = `${Config.SITE_TITLE} - Acesso`;
const DESC =
  "Entre ou crie sua conta na Deli da Persy para reservas e assinaturas.";
const CANONICAL = `${Config.SITE_DOMAIN}/login`;

function LoginPage() {
  const navigate = useNavigate();
  const { user, loading } = useContext(AuthContext);
  const { handleRedirect } = useAuthRedirect();

  // LÓGICA DE REDIRECIONAMENTO INTACTA
  useEffect(() => {
    if (!loading) {
      if (user !== null) {
        if (user.isNewUser) {
          navigate("/signup");
        } else {
          handleRedirect();
        }
      }
    }
  }, [user, loading, navigate, handleRedirect]);

  // ESTADO DE CARREGAMENTO MELHORADO (Centered Spinner)
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FFF5EE]">
        <div className="w-12 h-12 border-4 border-[#dbcdac] border-t-[#971253] rounded-full animate-spin mb-6"></div>
        <p className="text-[#528a89] font-serif text-xl animate-pulse">
          Preparando a mesa...
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FFF5EE] p-4 md:p-8 relative overflow-hidden">
      <title>{TITLE}</title>
      <link rel="icon" href={ICON} />
      <link rel="canonical" href={CANONICAL} />
      <meta name="description" content={DESC} />

      {/* Background Texture Vintage */}
      <div className="absolute top-0 left-0 w-full h-full opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/aged-paper.png')]" />

      {/* SPLIT LAYOUT CONTAINER */}
      <div className="relative w-full max-w-5xl flex flex-col md:flex-row bg-white rounded-[2rem] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] overflow-hidden z-10 animate-in fade-in zoom-in-[0.98] duration-500">
        {/* LADO ESQUERDO: Branding & Welcome */}
        <div className="w-full md:w-5/12 bg-gradient-to-br from-[#528a89] to-[#3a6362] p-10 md:p-12 flex flex-col items-center justify-center text-center relative overflow-hidden">
          {/* Efeito de luz ao fundo */}
          <div className="absolute -top-20 -left-20 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-[#dbcdac]/10 rounded-full blur-3xl pointer-events-none"></div>

          <img
            src={logoImg}
            alt="Deli da Persy Logo"
            className="w-48 md:w-64 mb-8 drop-shadow-2xl relative z-10"
          />

          <div className="relative z-10 text-white">
            <h2 className="font-serif text-3xl md:text-4xl mb-4 text-[#dbcdac]">
              Bem-vindo à Deli
            </h2>
            <p className="font-light text-white/90 text-sm md:text-base leading-relaxed max-w-xs mx-auto">
              Seu espaço para jogos, cafés e experiências incríveis.
            </p>
          </div>
        </div>

        {/* LADO DIREITO: Action & Login */}
        <div className="w-full md:w-7/12 p-8 md:p-14 flex flex-col justify-center bg-[#FFF5EE]/30">
          <div className="max-w-md mx-auto w-full">
            {/* Título da Ação */}
            <div className="mb-10 text-center md:text-left">
              <h1 className="font-serif text-3xl md:text-4xl text-[#971253] font-bold mb-3">
                Acesso a Área do Cliente
              </h1>
              <p className="text-stone-500 font-light text-sm md:text-base leading-relaxed">
                Sua experiência exclusiva começa aqui, um espaço pensado para
                quem faz parte. Conecte-se com sua conta Google de forma simples
                e protegida.
              </p>
            </div>

            {/* Wrapper do Botão Google (Destaque principal) */}
            <div className="bg-white p-5 md:p-8 rounded-2xl border border-[#dbcdac]/50 shadow-sm mb-8 md:mb-10 flex flex-col items-center hover:border-[#528a89]/50 transition-colors mx-auto w-full">
              <GoogleSignInButton />
            </div>

            {/* Bloco Suave de Benefícios */}
            <div className="bg-[#528a89]/5 rounded-2xl p-6 border border-[#528a89]/10">
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#528a89] mb-4 flex items-center gap-2">
                <Sparkles size={14} className="text-[#dbcdac]" /> Vantagens da
                Conta
              </h3>
              <ul className="space-y-3 m-0 p-0 list-none">
                <li className="flex items-center gap-3 text-stone-600 text-sm font-medium">
                  <CheckCircle2 size={18} className="text-[#528a89] shrink-0" />
                  Personalize sua experiência
                </li>
                <li className="flex items-center gap-3 text-stone-600 text-sm font-medium">
                  <CheckCircle2 size={18} className="text-[#528a89] shrink-0" />
                  Assine planos exclusivos
                </li>
                <li className="flex items-center gap-3 text-stone-600 text-sm font-medium">
                  <CheckCircle2 size={18} className="text-[#528a89] shrink-0" />
                  Reserve a sua mesa online
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
